import { defineStore } from 'pinia';
import { prettifyMixedCase } from '../../shared/Util';

export const useDossierStore = defineStore('dossier', {
  state: () => ({
    schema: {},
    currentValue: {},
    originalValue: {},
    resolvedProperties: {},
    hasUnsavedChanges: false,
    linkedMeetingCount: null,
    lastRefresh: null,
    isEditing: false,
  }),

  getters: {
    menuItems() {
      return Object.keys(this.schema?.properties || {}).map(id => {
        return {
          id,
          label: prettifyMixedCase(id),
        };
      });
    },
  },

  actions: {
    setSchema(newSchema) {
      this.schema = newSchema;
      this.resolvedProperties = this.resolveTopLevelProperties(newSchema);
    },
    setEditMode(newValue) {
      this.isEditing = newValue;
    },
    cancelChanges() {
      if (!this.hasUnsavedChanges) {
        this.isEditing = false;
        return;
      }

      if (!window.confirm('Are you sure you want to cancel your changes?')) {
        return;
      }

      this.currentValue = JSON.parse(JSON.stringify(this.originalValue));
      this.hasUnsavedChanges = false;
      this.isEditing = false;
    },
    setCurrentValue(newValue) {
      this.currentValue = newValue;
      this.hasUnsavedChanges = true;
    },
    setInitialValue(newValue) {
      this.currentValue = newValue;
      this.originalValue = JSON.parse(JSON.stringify(newValue));
      this.hasUnsavedChanges = false;
    },
    updateOriginalValue() {
      this.originalValue = JSON.parse(JSON.stringify(this.currentValue));
    },
    toggleUnsavedChanges(value) {
      this.hasUnsavedChanges = value !== undefined ? value : !this.hasUnsavedChanges;
    },
    resolveRefs(schema, definitions) {
      if (typeof schema !== 'object' || schema === null) return schema;

      if (schema.$ref) {
        const refKey = schema.$ref.split('/').pop();
        const resolvedSchema = { ...definitions[refKey], ...schema };
        delete resolvedSchema.$ref;
        return this.resolveRefs(resolvedSchema, definitions);
      }

      if (schema.anyOf || schema.allOf || schema.oneOf) {
        ['anyOf', 'allOf', 'oneOf'].forEach(key => {
          if (schema[key]) {
            schema[key] = schema[key].map(subschema => {
              if (subschema.items) {
                const subItems = this.resolveRefs(subschema.items, definitions);
                return this.resolveRefs({ ...subschema, items: subItems }, definitions);
              }
              return this.resolveRefs(subschema, definitions);
            });
          }
        });
      }

      if (schema.properties) {
        Object.keys(schema.properties).forEach(key => {
          schema.properties[key] = this.resolveRefs(
              schema.properties[key],
              definitions,
          );
        });
      }

      return schema;
    },
    generateEmptyObject(schema, defs = schema.$defs || {}, rootSchema = schema) {
      if (schema.$ref) {
        const refPath = schema.$ref.replace(/^#\/\$defs\//, '');
        return this.generateEmptyObject(defs[refPath], defs, rootSchema);
      }

      if (schema.anyOf) {
        return this.generateEmptyObject(schema.anyOf[0], defs, rootSchema); // default to first schema
      }

      if (schema.type === 'object') {
        const obj = {};
        const props = schema.properties || {};
        for (const key in props) {
          obj[key] = this.generateEmptyObject(props[key], defs, rootSchema);
        }
        return obj;
      }

      if (schema.type === 'array') {
        const itemSchema = schema.items || {};
        return [this.generateEmptyObject(itemSchema, defs, rootSchema)];
      }

      // Base types
      switch (schema.type) {
        case 'string': return '';
        case 'number':
        case 'integer': return 0;
        case 'boolean': return false;
        default: return null;
      }

      return null;
    },
    resolveTopLevelProperties(schema) {
      if (!schema || !schema.properties) return {};
      const definitions = schema.$defs || {};
      const resolved = {};

      Object.entries(schema.properties).forEach(([key, value]) => {
        resolved[key] = this.resolveRefs(value, definitions);
      });

      return resolved;
    },
  },
});
